<template>
    <div class="paginatedTable" :class="{ stickyHeader }">
        <div class="itemCount" v-if="$slots.itemsCountLabel">
            {{ filteredItems.length }}
            <slot name="itemsCountLabel"></slot>
        </div>

        <div class="overflowContainer" :style="minHeightStyle" ref="overflowContainer">
            <div v-if="!hasHeaders && $slots.noHeadersText" class="noHeadersText">
                <slot name="noHeadersText"></slot>
            </div>

            <table class="table table-striped table-hover" v-if="hasHeaders">
                <thead ref="tableHead">
                    <tr>
                        <th
                            v-for="(columnLabel, columnKey) in headers"
                            :key="columnKey"
                            :class="[columnKey, { headerNoWrap }]"
                        >
                            <slot
                                :name="getSpecificOrGeneralSlotName('headerCell', columnKey)"
                                :column-key="columnKey"
                                :column-label="columnLabel"
                                :filtered-items="filteredItems"
                            >
                                <div class="header">
                                    <i
                                        v-if="isSortable(columnKey)"
                                        class="fa headerIcon"
                                        :class="
                                            isSortedBy(columnKey)
                                                ? isAscendingSortOrder
                                                    ? 'fa-sort-asc'
                                                    : 'fa-sort-desc'
                                                : 'fa-sort'
                                        "
                                        @click="setSortByColumn(columnKey)"
                                    ></i>

                                    <table-header-filter
                                        @toggle-open="toggleFilter"
                                        :column-key="columnKey"
                                        :key="columnKey"
                                        :filter="filters[columnKey]"
                                        v-if="
                                            filters && filters[columnKey] && isColumnFilterable(filters[columnKey].type)
                                        "
                                    ></table-header-filter>

                                    <slot
                                        :name="getSpecificOrGeneralSlotName('headerLabel', columnKey)"
                                        :column-key="columnKey"
                                        :column-label="columnLabel"
                                        >{{ columnLabel }}</slot
                                    >
                                </div>
                            </slot>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-if="hasRowsSlot">
                        <slot name="rows" v-for="item in pageItems" :item="item"></slot>
                    </template>
                    <template v-else>
                        <tr
                            v-for="(item, index) in pageItems"
                            :key="getRowKey(item, index)"
                            :class="getRowClass(item)"
                            :ref="getItemRef(getItemKey(item))"
                            @click="onRowClick(item, index, $event)"
                        >
                            <td v-for="(_, columnKey) in headers" :key="columnKey" :class="columnKey">
                                <slot
                                    :name="getSpecificOrGeneralSlotName('cell', columnKey)"
                                    :item="item"
                                    :item-key="getItemKey(item)"
                                    :column-key="columnKey"
                                    >{{ item[columnKey] }}</slot
                                >
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <nav class="paginationNav" v-if="filteredItems.length > pageSize">
            <ul class="pagination">
                <li :class="{ disabled: isFirstPage }">
                    <a @click="setActivePage(0)">
                        <i class="fa fa-angle-double-left"></i>
                    </a>
                </li>
                <li :class="{ disabled: isFirstPage }">
                    <a @click="switchToPrevPage()">
                        <i class="fa fa-angle-left"></i>
                    </a>
                </li>
                <li v-if="limitedPages[0] !== 0">
                    <a class="nonClickable">...</a>
                </li>
                <li v-for="index in limitedPages" :key="index" :class="{ active: isActivePage(index) }">
                    <a @click="setActivePage(index)">{{ index + 1 }}</a>
                </li>
                <li v-if="limitedPages[limitedPages.length - 1] !== numberOfPages - 1">
                    <a class="nonClickable">...</a>
                </li>
                <li :class="{ disabled: isLastPage }">
                    <a @click="switchToNextPage()">
                        <i class="fa fa-angle-right"></i>
                    </a>
                </li>
                <li :class="{ disabled: isLastPage }">
                    <a @click="setActivePage(numberOfPages - 1)">
                        <i class="fa fa-angle-double-right"></i>
                    </a>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script>
import TableHeaderFilter from './filters/TableHeaderFilter';
import { COLUMN_TYPE, isColumnFilterable } from './columnTypes';

const PAGINATOR_NUM_PAGES = 11; // should be odd

const SORT_ORDER = {
    ASCENDING: 'ASCENDING',
    DESCENDING: 'DESCENDING'
};

export default {
    name: 'PaginatedTable',
    components: { TableHeaderFilter },
    props: {
        headers: {
            type: Object,
            required: true
        },
        items: {
            type: Array,
            required: true
        },
        itemKey: {
            type: String,
            default: null
        },
        sortFormatter: {
            type: Function,
            default: (row, key) => (row ? row[key] : '')
        },
        pageSize: {
            type: [Number, String],
            default: 10
        },
        stickyHeader: {
            type: Boolean,
            default: true
        },
        headerNoWrap: {
            type: Boolean,
            default: false
        },
        minHeight: {
            type: String,
            default: null
        },
        height: {
            type: String,
            default: null
        },
        sortingDisabledFor: {
            type: Array,
            default: () => []
        },
        initialSortByColumn: {
            type: String,
            default: () => null
        },
        filters: {
            type: Object,
            required: false,
            default: () => null
        },
        useCustomFilter: {
            type: Boolean,
            required: false,
            default: () => false
        },
        highlightedRows: {
            type: Array,
            default: null
        }
    },

    data() {
        return {
            activePage: 0,
            sortByColumn: this.initialSortByColumn || Object.keys(this.headers)[0],
            sortOrder: SORT_ORDER.ASCENDING,
            openFilters: {}
        };
    },

    computed: {
        filteredItems() {
            if (this.filters === null || this.useCustomFilter) {
                return this.items;
            }

            return this.items.filter(row => {
                for (const [key, filterEntry] of Object.entries(this.filters)) {
                    if (!filterEntry.doesMatch(row[key])) {
                        return false;
                    }
                }
                return true;
            });
        },

        hasHeaders() {
            return Object.keys(this.headers).length > 0;
        },

        hasRowsSlot() {
            return !!this.$scopedSlots.rows;
        },

        sortedItems() {
            if (!this.filteredItems) {
                return [];
            }

            const columnKey = this.sortByColumn;
            const sortOrder = this.sortOrder;

            return this.filteredItems.slice().sort((first, second) => {
                const firstValue = this.sortFormatter(first, columnKey);
                const secondValue = this.sortFormatter(second, columnKey);

                // check for undefined to move missing values to the end when sorting ascendingly
                if (firstValue === undefined || firstValue > secondValue) {
                    return sortOrder === SORT_ORDER.ASCENDING ? 1 : -1;
                } else if (secondValue === undefined || secondValue > firstValue) {
                    return sortOrder === SORT_ORDER.ASCENDING ? -1 : 1;
                }
                return 0;
            });
        },

        pageItems() {
            const firstItemIndex = this.activePage * this.pageSize;
            const lastItemIndex = this.activePage * this.pageSize + (this.pageSize - 1);
            return this.sortedItems.slice(firstItemIndex, lastItemIndex + 1);
        },

        isAscendingSortOrder() {
            return this.sortOrder === SORT_ORDER.ASCENDING;
        },

        numberOfPages() {
            const numberOfPages = Math.ceil(this.sortedItems.length / this.pageSize);
            return isNaN(numberOfPages) ? 0 : numberOfPages;
        },

        pagesCount() {
            const count = this.numberOfPages;
            return Array.from(new Array(count).keys());
        },

        limitedPages() {
            const numPages = this.numberOfPages;
            if (numPages < PAGINATOR_NUM_PAGES) {
                return this.pagesCount;
            }

            // Display 11 pages, ideally 5 to the left and 5 to the right of the active page
            const numPagesLeftRight = Math.floor(PAGINATOR_NUM_PAGES / 2);
            const startIndex = Math.max(
                0,
                Math.min(numPages - PAGINATOR_NUM_PAGES, this.activePage - numPagesLeftRight)
            );
            return this.pagesCount.slice(startIndex, startIndex + PAGINATOR_NUM_PAGES);
        },

        isFirstPage() {
            return this.activePage === 0;
        },

        isLastPage() {
            return this.activePage === this.numberOfPages - 1;
        },

        minHeightStyle() {
            if (this.height !== null) {
                return { height: `calc(100vh - ${this.height} - 270px` };
            }
            if (this.minHeight === null) {
                return null;
            }

            return { 'min-height': this.minHeight };
        },
        openFilter() {
            const foundFilterKey = Object.keys(this.openFilters).find(key => this.openFilters[key] === true);
            if (foundFilterKey === undefined) {
                return null;
            }
            return foundFilterKey;
        }
    },

    watch: {
        filters: {
            handler(filters) {
                // in case the active page is > 0 filter result won't be visible
                this.setActivePage(0);

                if (!this.useCustomFilter) {
                    return;
                }
                if (this.openFilter === null) {
                    return;
                }
                // TODO : move to own function - use proper naming
                const updatedFilter = Object.assign({}, filters[this.openFilter]);
                if (updatedFilter.type === COLUMN_TYPE.enum) {
                    updatedFilter.model = updatedFilter.model.filter(item => item.selected).map(item => item.item);
                }
                if (typeof updatedFilter.model === 'object') {
                    updatedFilter.model = [updatedFilter.model.from, updatedFilter.model.to];
                }
                this.$emit('filter-change', { filterKey: this.openFilter, ...updatedFilter });
            },
            deep: true
        }
    },

    methods: {
        getItemKey(item) {
            const key = this.itemKey ? item[this.itemKey] : null;
            const hasKey = key !== null && key !== undefined && key !== '';
            if (process.env.NODE_ENV === 'development') {
                if (!hasKey && this.itemKey) {
                    // eslint-disable-next-line no-console
                    console.warn(`[PaginatedTable] Item has no key at property ${this.itemKey}`, item);
                }
            }
            return hasKey ? key : null;
        },

        getRowKey(item, indexInPageItems) {
            const key = this.getItemKey(item);
            return key !== null ? key : indexInPageItems;
        },

        setActivePage(index) {
            this.activePage = Math.max(0, Math.min(this.numberOfPages - 1, index));
        },

        isActivePage(index) {
            return this.activePage === index;
        },

        switchToPrevPage() {
            if (!this.isFirstPage) {
                this.setActivePage(this.activePage - 1);
            }
        },

        switchToNextPage() {
            if (!this.isLastPage) {
                this.setActivePage(this.activePage + 1);
            }
        },

        setSortByColumn(columnKey) {
            if (this.sortByColumn === columnKey) {
                this.sortOrder =
                    this.sortOrder === SORT_ORDER.DESCENDING ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDING;
            } else {
                this.sortByColumn = columnKey;
                this.sortOrder = SORT_ORDER.ASCENDING;
            }
        },

        isSortedBy(columnKey) {
            return this.sortByColumn === columnKey;
        },

        isSortable(columnKey) {
            return !this.sortingDisabledFor.includes(columnKey);
        },

        isColumnFilterable(type) {
            return type && isColumnFilterable(type);
        },

        toggleFilter(filter) {
            this.openFilters = {
                ...this.openFilters,
                ...filter
            };
        },

        onRowClick(item, indexInPageItems, nativeEvent) {
            this.$emit('row-click', item, indexInPageItems, nativeEvent);
        },

        isRowHighlighted(item) {
            if (this.highlightedRows && this.highlightedRows.length > 0) {
                const key = this.getItemKey(item);
                return this.highlightedRows.find(itemOrKey => itemOrKey === key || itemOrKey === item) !== undefined;
            }
            return false;
        },

        getRowClass(item) {
            return this.isRowHighlighted(item) ? 'highlight' : null;
        },

        getItemRef(itemKey) {
            if (itemKey === null) {
                return null;
            }
            return `item-${itemKey}`;
        },

        getElemByItemKey(itemKey) {
            const elem = this.$refs[this.getItemRef(itemKey)];
            if (!elem || elem.length !== 1) {
                console.error(`Can't find item with key: ${itemKey}`); // eslint-disable-line
                return null;
            }
            return elem[0];
        },

        scrollToItem(itemKey) {
            const element = this.getElemByItemKey(itemKey);
            if (element && !this.isElemVisible(element)) {
                const { offsetTop } = element;
                this.$refs.overflowContainer.scrollTop = offsetTop - this.$refs.tableHead.clientHeight;
            }
        },

        isElemVisible(element) {
            const { offsetTop } = element;
            const { scrollTop, clientHeight } = this.$refs.overflowContainer;
            return offsetTop >= scrollTop + this.$refs.tableHead.clientHeight && offsetTop <= scrollTop + clientHeight;
        },

        getSpecificOrGeneralSlotName(slotName, columnKey) {
            const specificSlot = `${slotName}-${columnKey}`;
            return this.$scopedSlots[specificSlot] ? specificSlot : slotName;
        }
    }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/settings';

.paginatedTable {
    // Use Bootstrap styles but make this component usable outside of a bootstrap context
    @import '~bootstrap-sass/assets/stylesheets/bootstrap/variables';
    @import '~bootstrap-sass/assets/stylesheets/bootstrap/mixins';
    @import '~bootstrap-sass/assets/stylesheets/bootstrap/pagination';
    @import '~bootstrap-sass/assets/stylesheets/bootstrap/tables';

    .itemCount {
        font-weight: bold;
        text-align: center;
        margin-bottom: 15px;
    }

    .noHeadersText {
        font-weight: bold;
        text-align: center;
    }

    .overflowContainer {
        width: 100%;
        overflow-x: auto;
    }

    table {
        border-collapse: collapse;
        border-spacing: 0;
        margin-bottom: 0 !important;

        th {
            vertical-align: middle;
            padding: 0 15px 0 0;

            &.headerNoWrap {
                white-space: nowrap;
            }

            .header {
                display: flex;
                flex-direction: row;

                .headerIcon {
                    margin-top: 3px;
                    margin-right: 5px;

                    &:hover {
                        color: $color_gray;
                        cursor: pointer;
                    }
                }
            }
        }

        td {
            padding: 0;

            .fa {
                cursor: pointer;
                margin-right: 10px;
                &.fa-edit,
                &.fa-close {
                    margin-right: 0;
                }

                &:hover {
                    color: $color_gray;
                }
            }
        }
    }

    &.stickyHeader {
        .overflowContainer {
            height: 57vh;
            overflow-y: auto;
        }

        table {
            th {
                top: 0;
                position: sticky;
                background-color: $color_white;
                z-index: $z-index-raised-1;
                box-shadow: 0 1px $color_gray;
            }
        }
    }

    .paginationNav {
        text-align: center;
    }

    .pagination {
        li {
            color: $color_ci_blue_shade1;
            cursor: pointer;

            a {
                background-color: $color_white;
                color: inherit;
                border-color: $color_ci_blue_shade4;
            }
            a:hover {
                color: inherit;
                background-color: $color_ci_blue_shade4;
                border-color: $color_ci_blue_shade4;
            }
        }

        li.active {
            a,
            a:hover {
                background-color: $color_ci_blue_shade1;
                border-color: $color_ci_blue_shade1;
                color: $color_white;
            }
        }

        .nonClickable {
            pointer-events: none;
        }
    }

    // additional selectors to `.highlight` to win specificity over bootstrap
    tbody tr.highlight {
        background: $color_ci_blue_shade4;
    }
    .table-striped tbody tr.highlight:nth-of-type(odd) {
        background: darken($color_ci_blue_shade4, 8%);
    }
    .table-hover tbody tr.highlight:hover {
        background: darken($color_ci_blue_shade4, 16%);
    }
}
</style>
